import { serviceSingletonGetter } from '@/services/serviceGetter';

const ONLOAD_CALLBACK_NAME = '__onHCaptchaLoaded__';
const HCAPTCHA_API_URL = `https://js.hcaptcha.com/1/api.js?onload=${ONLOAD_CALLBACK_NAME}&render=explicit`;
const SCRIPT_TIMEOUT_MS = 10000;
const TOKEN_TIMEOUT_MS = 15000;

export class HCaptchaScriptLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HCaptchaScriptLoadError';
    Object.setPrototypeOf(this, HCaptchaScriptLoadError.prototype);
  }
}

declare global {
  interface Window {
    __onHCaptchaLoaded__?: () => void;
    hcaptcha?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          size: string;
          callback?: (token: string) => void;
        }
      ) => string;
      execute: (
        widgetId: string,
        options?: { async?: boolean }
      ) => Promise<{ response: string }>;
      remove: (widgetId: string) => void;
    };
  }
}

export type HCaptchaService = {
  getToken: () => Promise<string | undefined>;
};

let scriptPromise: Promise<NonNullable<Window['hcaptcha']>> | undefined;

function loadHCaptcha(): Promise<NonNullable<Window['hcaptcha']>> {
  if (window.hcaptcha) return Promise.resolve(window.hcaptcha);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    let scriptTimeoutId: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      delete window[ONLOAD_CALLBACK_NAME];
      scriptPromise = undefined;
      if (scriptTimeoutId !== undefined) {
        clearTimeout(scriptTimeoutId);
      }
    };

    window[ONLOAD_CALLBACK_NAME] = () => {
      const { hcaptcha } = window;
      cleanup();
      if (hcaptcha) {
        resolve(hcaptcha);
      } else {
        reject(new HCaptchaScriptLoadError('hCaptcha failed to initialize'));
      }
    };

    const script = document.createElement('script');
    script.src = HCAPTCHA_API_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      cleanup();
      script.remove();
      reject(new HCaptchaScriptLoadError('hCaptcha failed to load'));
    };
    scriptTimeoutId = setTimeout(() => {
      cleanup();
      script.remove();
      reject(new HCaptchaScriptLoadError('hCaptcha failed to load'));
    }, SCRIPT_TIMEOUT_MS);
    document.head.appendChild(script);
  });
  return scriptPromise;
}

async function executeHCaptcha(
  siteKey: string,
  timeoutMs: number = TOKEN_TIMEOUT_MS
): Promise<string> {
  const hcaptcha = await loadHCaptcha();
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.width = '1px';
  container.style.height = '1px';
  container.style.opacity = '0';
  container.style.pointerEvents = 'none';
  document.body.appendChild(container);

  let widgetId: string | undefined;
  let isCleanedUp = false;

  const cleanup = () => {
    if (isCleanedUp) return;
    isCleanedUp = true;
    if (widgetId !== undefined) {
      try {
        hcaptcha.remove(widgetId);
      } catch {
        container.remove();
      }
    }
    container.remove();
  };

  try {
    widgetId = hcaptcha.render(container, {
      sitekey: siteKey,
      size: 'invisible',
    });

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('hCaptcha execution timed out'));
      }, timeoutMs);
    });

    const executePromise = hcaptcha
      .execute(widgetId, { async: true })
      .then((result) => result.response);

    const response = await Promise.race([executePromise, timeoutPromise]);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    return response;
  } finally {
    cleanup();
  }
}

export const createHCaptchaService = (): HCaptchaService => {
  const getToken = async (): Promise<string | undefined> => {
    const siteKey = process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY;
    if (!siteKey) {
      return undefined;
    }

    return executeHCaptcha(siteKey);
  };

  return { getToken };
};

export default serviceSingletonGetter(
  Symbol('hcaptchaService'),
  createHCaptchaService
);
